import React, { useState, useEffect, useCallback } from "react";
import { customFetch } from '../apiClient';
import * as XLSX from "xlsx"; 

import defaultLogo from "../assests/kamlaLogo.png"; 

const SalesReports = () => {
  const [activeTab, setActiveTab] = useState("daily");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null); 
  
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    search: "",
    customerType: "",
    transactionType: "" 
  });

  const [summary, setSummary] = useState({ 
    grossSales: 0, netSales: 0, returns: 0, cash: 0, upi: 0, bills: 0,
    dueCollected: 0, dueCollectionCash: 0, dueCollectionUpi: 0,
    opd: { gross: 0, received: 0, due: 0 },
    ipd: { gross: 0, received: 0, due: 0 },
    pharmacy: { gross: 0, received: 0, due: 0 }
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    const fetchCompanyInfo = async () => {
     try {
        const res = await customFetch('/PharmaCompany');
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0) setCompanyInfo(data[0]);
        }
      } catch (err) { console.error("Failed to fetch company info", err); }
    };
    fetchCompanyInfo();
  }, []);

  const fetchReport = async () => {
    setLoading(true); setError(""); setReportData([]); setCurrentPage(1); 
    try {
      const params = new URLSearchParams({
         fromDate: filters.fromDate,
         toDate: filters.toDate,
         ...(filters.search && { search: filters.search })
      });

      let endpoint = "";
      if (activeTab === "daily") endpoint = "daily";
      else if (activeTab === "patient") endpoint = "patient";
      else if (activeTab === "item-wise") endpoint = "item-wise";
      else if (activeTab === "user-wise") endpoint = "user-wise";

      const res = await customFetch(`/SalesReports/${endpoint}?${params.toString()}`);

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      
      let cleanData = data.map(item => ({
          ...item,
          transactionType: item.transactionType || item.TransactionType || 'SALE',
          customerType: item.customerType || item.CustomerType || 'MEDICUST',
      }));

      if (activeTab === 'daily' || activeTab === 'patient') {
          if (filters.transactionType) {
              cleanData = cleanData.filter(item => item.transactionType === filters.transactionType);
          }
          if (filters.customerType) {
              cleanData = cleanData.filter(item => item.customerType === filters.customerType);
          }
      }

      setReportData(cleanData);

      if (activeTab === "daily" || activeTab === "patient") {
          const validSalesData = cleanData.filter(
            item => item.transactionType !== 'RETURN' && item.transactionType !== 'DUE_COLLECT'
          );

          const totalReturns = cleanData
              .filter(item => item.transactionType === 'RETURN')
              .reduce((sum, item) => sum + Math.abs(item.totalBillAmount || 0), 0);

          const dueCollectionsData = cleanData.filter(item => item.transactionType === 'DUE_COLLECT');
          const dueCollected = dueCollectionsData.reduce((sum, item) => sum + (item.totalBillAmount || 0), 0);
          const dueCollectionCash = dueCollectionsData.reduce((sum, item) => sum + (item.cashAmount || 0), 0);
          const dueCollectionUpi = dueCollectionsData.reduce((sum, item) => sum + (item.upiAmount || 0), 0);

          const grossSales = validSalesData.reduce((sum, item) => sum + (item.totalBillAmount || 0), 0);
          
          const cash = cleanData.reduce((sum, item) => sum + (item.cashAmount || 0), 0);
          const upi = cleanData.reduce((sum, item) => sum + (item.upiAmount || 0), 0);

          const calculateCategory = (customerTypeCondition) => {
              return validSalesData.filter(customerTypeCondition).reduce((acc, item) => {
                  const billAmount = item.totalBillAmount || 0;
                  const received = (item.cashAmount || 0) + (item.upiAmount || 0);
                  const due = item.dueAmount !== undefined ? item.dueAmount : Math.max(0, billAmount - received);

                  return {
                      gross: acc.gross + billAmount,
                      received: acc.received + received,
                      due: acc.due + due
                  };
              }, { gross: 0, received: 0, due: 0 });
          };

          const opdStats = calculateCategory(item => item.customerType === 'OPD');
          const ipdStats = calculateCategory(item => item.customerType === 'IPD');
          const pharmacyStats = calculateCategory(item => item.customerType === 'MEDICUST' || item.customerType === 'PHARMACY');
          
          setSummary({ 
              grossSales: grossSales, 
              netSales: grossSales - totalReturns, 
              returns: totalReturns,
              cash: cash, 
              upi: upi, 
              bills: cleanData.length,
              dueCollected,
              dueCollectionCash,
              dueCollectionUpi,
              opd: opdStats,
              ipd: ipdStats,
              pharmacy: pharmacyStats
          });
      }
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, [activeTab]);

  const totalPages = Math.ceil(reportData.length / rowsPerPage) || 1;
  const displayedData = reportData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const exportToExcel = useCallback(() => {
    if (reportData.length === 0) { alert("No data available to export!"); return; }

    let formattedData = [];
    if (activeTab === 'daily') {
      formattedData = reportData.map((item, i) => ({
        "S.No": i + 1, 
        "Date": formatDate(item.billDate), 
        "Bill/Receipt No": item.billNo,
        "Txn Type": item.transactionType, 
        "Pat. Type": getPatType(item.customerType),
        "Patient Name": item.patientName, 
        "Pay Mode": item.payMode,
        "Cash (₹)": item.cashAmount, 
        "UPI (₹)": item.upiAmount, 
        "Total (₹)": item.totalBillAmount, 
        "User": item.userName
      }));
    } else if (activeTab === 'patient') {
      formattedData = reportData.map((item, i) => ({
        "S.No": i + 1, 
        "Date": formatDate(item.billDate), 
        "Bill/Receipt No": item.billNo,
        "Txn Type": item.transactionType, 
        "Pat. Type": getPatType(item.customerType),
        "Patient Name": item.patientName, 
        "Mobile": item.mobileNo,
        "Net Amount (₹)": item.totalBillAmount, 
        "Due Amount (₹)": item.dueAmount
      }));
    } else if (activeTab === 'item-wise') {
      formattedData = reportData.map((item, i) => ({
        "Rank": i + 1, "Medicine Name": item.medicine, "Unit": item.unit,
        "Times Sold": item.timesSold, "Qty Sold": item.totalQtySold, "Revenue (₹)": item.totalRevenue
      }));
    } else if (activeTab === 'user-wise') {
      formattedData = reportData.map((item, i) => ({
        "S.No": i + 1, "Staff Name": item.userName, "Total Bills/Receipts": item.totalBills,
        "Cash Collected (₹)": item.totalCashCollection, 
        "UPI Collected (₹)": item.totalUPICollection, 
        "Total Activity (₹)": item.totalSaleValue
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `${activeTab.toUpperCase()}_REPORT_${filters.fromDate}_to_${filters.toDate}.xlsx`);
  }, [reportData, activeTab, filters]);

  const handlePrint = useCallback(() => {
    if (reportData.length === 0) { alert("No data available to print!"); return; }

    const logoSource = companyInfo?.logoBase64 ? `data:image/png;base64,${companyInfo.logoBase64}` : defaultLogo;
    const compName = companyInfo?.company_Name || "HOSPITAL / PHARMACY STORE";
    const compAddress = companyInfo?.address || "";
    const compContact = companyInfo?.contact1 ? `Ph: ${companyInfo.contact1}` : "";
    const compGst = companyInfo?.reg_No ? `DL/GST: ${companyInfo.reg_No}` : "";

    const ROWS_PER_PAGE = 35; 
    const pages = [];
    let runningTotal = 0;

    for (let i = 0; i < reportData.length; i += ROWS_PER_PAGE) {
      const pageItems = reportData.slice(i, i + ROWS_PER_PAGE);
      const broughtForward = runningTotal;
      
      const pageTotal = pageItems.reduce((sum, item) => {
          if (activeTab === 'item-wise') return sum + (item.totalRevenue || 0);
          if (activeTab === 'user-wise') return sum + (item.totalSaleValue || 0);
          return sum + (item.totalBillAmount || 0);
      }, 0);

      runningTotal += pageTotal;
      
      pages.push({
        items: pageItems,
        startIndex: i,
        broughtForward: broughtForward,
        carriedForward: runningTotal,
        isLastPage: i + ROWS_PER_PAGE >= reportData.length
      });
    }

    const reportHtml = pages.map((page, pageIndex) => {
      
      let thHtml = "";
      if (activeTab === 'daily') thHtml = `<th style="text-align:left">Date</th><th style="text-align:left">Bill/Receipt No</th><th style="text-align:center">Txn Type</th><th style="text-align:center">Pat. Type</th><th style="text-align:left">Patient Name</th><th>Mode</th><th style="text-align:right">Amount</th>`;
      else if (activeTab === 'patient') thHtml = `<th style="text-align:left">Date</th><th style="text-align:left">Bill/Receipt No</th><th style="text-align:center">Txn Type</th><th style="text-align:center">Pat. Type</th><th style="text-align:left">Patient Name</th><th>Mobile</th><th style="text-align:right">Amount</th>`;
      else if (activeTab === 'item-wise') thHtml = `<th style="text-align:left">Rank</th><th style="text-align:left">Medicine Name</th><th style="text-align:left">Unit</th><th style="text-align:center">Times Sold</th><th style="text-align:center">Qty Sold</th><th style="text-align:right">Revenue</th>`;
      else if (activeTab === 'user-wise') thHtml = `<th style="text-align:left">Staff Name</th><th style="text-align:center">Total</th><th style="text-align:right">Cash</th><th style="text-align:right">UPI</th><th style="text-align:right">Total Activity</th>`;

      const trHtml = page.items.map((item, idx) => {
        if (activeTab === 'daily') return `<tr><td style="text-align:left">${formatDate(item.billDate)}</td><td style="text-align:left">${item.billNo}</td><td style="text-align:center">${item.transactionType}</td><td style="text-align:center">${getPatType(item.customerType)}</td><td style="text-align:left">${item.patientName}</td><td style="text-align:center">${item.payMode}</td><td style="text-align:right; font-weight:bold;">${item.totalBillAmount?.toFixed(2)}</td></tr>`;
        if (activeTab === 'patient') return `<tr><td style="text-align:left">${formatDate(item.billDate)}</td><td style="text-align:left">${item.billNo}</td><td style="text-align:center">${item.transactionType}</td><td style="text-align:center">${getPatType(item.customerType)}</td><td style="text-align:left">${item.patientName}</td><td style="text-align:center">${item.mobileNo || '-'}</td><td style="text-align:right; font-weight:bold;">${item.totalBillAmount?.toFixed(2)}</td></tr>`;
        if (activeTab === 'item-wise') return `<tr><td style="text-align:left">${page.startIndex + idx + 1}</td><td style="text-align:left">${item.medicine}</td><td style="text-align:left">${item.unit}</td><td style="text-align:center">${item.timesSold}</td><td style="text-align:center">${item.totalQtySold}</td><td style="text-align:right; font-weight:bold;">${item.totalRevenue?.toFixed(2)}</td></tr>`;
        if (activeTab === 'user-wise') return `<tr><td style="text-align:left">${item.userName}</td><td style="text-align:center">${item.totalBills}</td><td style="text-align:right">${item.totalCashCollection?.toFixed(2)}</td><td style="text-align:right">${item.totalUPICollection?.toFixed(2)}</td><td style="text-align:right; font-weight:bold;">${item.totalSaleValue?.toFixed(2)}</td></tr>`;
      }).join('');

      const colSpanBF = activeTab === 'user-wise' ? '4' : activeTab === 'item-wise' ? '5' : '6';

      const bfHtml = pageIndex > 0 ? `
        <tr style="background:#f9f9f9; border-bottom: 1px solid #000;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-style:italic;">Brought Forward (B/F):</td>
          <td style="text-align:right; font-weight:bold;">${page.broughtForward.toFixed(2)}</td>
        </tr>` : "";

      const cfHtml = `
        <tr style="background:#e0e0e0;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">
             ${page.isLastPage ? 'GRAND TOTAL (This Column):' : 'Carried Forward (C/F):'}
          </td>
          <td style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">${page.carriedForward.toFixed(2)}</td>
        </tr>`;

      const showFinalSummary = page.isLastPage && (activeTab === 'daily' || activeTab === 'patient');
      const finalSummaryHtml = showFinalSummary ? `
        <div style="display: flex; justify-content: flex-end; padding: 10px 15px; background: #fff; border-top: 1px dashed #000;">
          <table style="width: 320px; border-collapse: collapse; font-size: 13px;">
            <tbody>
              <tr>
                <td style="padding: 4px 0;">Total Sales (Gross):</td>
                <td style="text-align: right; padding: 4px 0; font-weight:bold;">₹ ${summary.grossSales.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 4px 0; color: #dc2626;">Total Returns (-):</td>
                <td style="text-align: right; padding: 4px 0; color: #dc2626; font-weight:bold;">₹ ${summary.returns.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 4px 0; color: #7c3aed;">Due Collections:</td>
                <td style="text-align: right; padding: 4px 0; color: #7c3aed; font-weight:bold;">₹ ${summary.dueCollected.toFixed(2)}</td>
              </tr>
              <tr style="border-top: 1px solid #000; border-bottom: 2px solid #000; background: #f8f9fa;">
                <td style="padding: 8px 5px; font-weight: bold; font-size: 14px;">Net Sales:</td>
                <td style="text-align: right; padding: 8px 5px; font-weight: bold; font-size: 14px;">₹ ${summary.netSales.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 4px 0; padding-top: 8px;">Total Cash (Sale+Due):</td>
                <td style="text-align: right; padding: 4px 0; padding-top: 8px; font-weight:bold;">₹ ${summary.cash.toFixed(2)}</td>
              </tr>
              <tr>
                <td style="padding: 4px 0;">Total UPI (Sale+Due):</td>
                <td style="text-align: right; padding: 4px 0; font-weight:bold;">₹ ${summary.upi.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ` : "";

      return `
        <div class="a4-page ${page.isLastPage ? 'last-page' : ''}">
          
          <div class="marg-header">
            <img src="${logoSource}" alt="Logo" class="print-logo"/>
            <div class="marg-header-center">
              <h2>${compName}</h2>
              <p>${compAddress}</p>
              <p><strong>${compContact}</strong></p>
            </div>
            <div class="marg-header-right">
              ${compGst ? `<span><strong>${compGst}</strong></span><br/>` : ""}
              <span><strong>Page:</strong> ${pageIndex + 1} of ${pages.length}</span><br/>
              <span><strong>Print Date:</strong> ${new Date().toLocaleDateString('en-GB')}</span>
            </div>
          </div>
          
          <div class="marg-title-strip">
            ${activeTab.toUpperCase().replace('-', ' ')} REPORT 
            (FROM: ${formatDate(filters.fromDate)} TO: ${formatDate(filters.toDate)})
          </div>

          <div class="content-area">
            <table class="items-table">
              <thead><tr>${thHtml}</tr></thead>
              <tbody>${bfHtml}${trHtml}</tbody>
            </table>
          </div>
          
          <div class="footer-area">
             <table class="items-table" style="margin:0; border-top: 2px solid #000;"><tbody>${cfHtml}</tbody></table>
             ${finalSummaryHtml}
          </div>
          
        </div>
      `;
    }).join("");

    const pw = window.open("", "_blank", "width=900,height=700");
    pw.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${activeTab.toUpperCase()} Report</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
          body { background: #525659; display: flex; flex-direction: column; align-items: center; padding: 20px; }
          
          @media print {
            @page { size: A4 portrait; margin: 8mm; }
            body { background: #fff; padding: 0; margin: 0;}
            .a4-page { 
               width: 100% !important;
               min-height: 275mm;
               page-break-after: always; 
               margin: 0 !important; 
               border: 1px solid #000 !important;
            }
            .a4-page.last-page { page-break-after: auto; }
          }
          
          .a4-page { 
            background: #fff; 
            width: 210mm; 
            min-height: 280mm; 
            border: 2px solid #000; 
            margin-bottom: 20mm; 
            display: flex; 
            flex-direction: column; 
            box-shadow: 0 0 10px rgba(0,0,0,0.5);
          }
          
          .marg-header { border-bottom: 2px solid #000; padding: 10px 15px; display: flex; justify-content: space-between; align-items: center; }
          .print-logo { width: 75px; height: 75px; object-fit: contain; flex-shrink:0;}
          .marg-header-center { flex-grow: 1; padding: 0 15px; text-align:center;}
          .marg-header-center h2 { margin: 0 0 4px 0; font-size: 18px; text-transform: uppercase; }
          .marg-header-center p { margin: 0; font-size: 12px; }
          .marg-header-right { text-align: right; font-size: 11px; line-height: 1.5; flex-shrink:0;}
          
          .marg-title-strip { background-color: #e0e0e0 !important; -webkit-print-color-adjust: exact !important; border-bottom: 2px solid #000; text-align: center; padding: 5px; font-weight: bold; font-size: 13px; }
          
          .content-area { flex-grow: 1; padding: 0; } 
          .footer-area { margin-top: auto; }
          
          .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .items-table th { border-bottom: 2px solid #000; border-right: 1px solid #ccc; background-color: #f1f1f1 !important; -webkit-print-color-adjust: exact !important; padding: 6px 8px; font-weight: bold; text-transform: uppercase;}
          .items-table th:last-child { border-right: none; }
          .items-table td { border-right: 1px dashed #ccc; border-bottom: 1px dashed #ccc; padding: 5px 8px; }
          .items-table td:last-child { border-right: none; }
        </style>
      </head>
      <body>${reportHtml}</body>
      </html>
    `);
    pw.document.close();
    setTimeout(() => { pw.focus(); pw.print(); }, 500);
  }, [reportData, activeTab, filters, companyInfo, summary]); 

  useEffect(() => {
    const handleExportEvent = (e) => {
      const exportType = e.detail?.type;
      if (exportType === 'excel') exportToExcel();
      else if (exportType === 'pdf' || exportType === 'print') handlePrint(); 
    };

    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [exportToExcel, handlePrint]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  const getPatType = (type) => {
      if (!type) return 'Pharmacy';
      if (type.toUpperCase() === 'MEDICUST') return 'MCDC';
      return type.toUpperCase();
  };

  const getTxnTypeBadgeStyle = (txnType) => {
    if (txnType === 'RETURN') {
      return { bg: '#fee2e2', color: '#dc2626', border: '#f87171' };
    }
    if (txnType === 'DUE_COLLECT') {
      return { bg: '#f3e8ff', color: '#7c3aed', border: '#c4b5fd' };
    }
    return { bg: '#dcfce7', color: '#16a34a', border: '#86efac' };
  };

  const getTxnTypeLabel = (txnType) => {
    if (txnType === 'DUE_COLLECT') return 'DUE COLL';
    return txnType;
  };

  return (
    <div className="report-container">
      <div className="report-card">
        
        <div className="card-header">
          <div className="header-left">
             <div className="header-icon"><i className="bi bi-graph-up-arrow"></i></div>
             <div className="header-text"><h5>Sales Reports</h5><p>Daily Sales, Returns & Due Collections</p></div>
          </div>
        </div>

        <div className="tabs-header">
           <button className={`tab-btn ${activeTab === 'daily' ? 'active' : ''}`} onClick={() => setActiveTab('daily')}><i className="bi bi-calendar-check"></i> Day Book</button>
           <button className={`tab-btn ${activeTab === 'patient' ? 'active' : ''}`} onClick={() => setActiveTab('patient')}><i className="bi bi-person-lines-fill"></i> Patient Wise</button>
           <button className={`tab-btn ${activeTab === 'item-wise' ? 'active' : ''}`} onClick={() => setActiveTab('item-wise')}><i className="bi bi-capsule"></i> Item Wise</button>
           <button className={`tab-btn ${activeTab === 'user-wise' ? 'active' : ''}`} onClick={() => setActiveTab('user-wise')}><i className="bi bi-people"></i> Staff Wise</button>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger">{error}</div>}

          <div className="filter-bar">
             <div className="form-group">
                <label>From Date</label>
                <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} className="form-input" />
             </div>
             <div className="form-group">
                <label>To Date</label>
                <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} className="form-input" />
             </div>
             
             {(activeTab === 'daily' || activeTab === 'patient') && (
                 <>
                   <div className="form-group">
                      <label>Txn Type</label>
                      <select name="transactionType" value={filters.transactionType} onChange={handleFilterChange} className="form-input">
                         <option value="">All</option>
                         <option value="SALE">Sale</option>
                         <option value="RETURN">Return</option>
                         <option value="DUE_COLLECT">Due Collection</option>
                      </select>
                   </div>
                   <div className="form-group">
                      <label>Patient Type</label>
                      <select name="customerType" value={filters.customerType} onChange={handleFilterChange} className="form-input">
                         <option value="">All Types</option>
                         <option value="OPD">OPD</option>
                         <option value="IPD">IPD</option>
                         <option value="MEDICUST">Pharmacy</option>
                      </select>
                   </div>
                 </>
             )}
             {activeTab === 'patient' && (
                 <div className="form-group" style={{flex:2}}>
                    <label>Search Patient</label>
                    <input type="text" name="search" value={filters.search} onChange={handleFilterChange} className="form-input" placeholder="Name or Mobile..." />
                 </div>
             )}
             <button className="btn-search" onClick={fetchReport} disabled={loading}>
                {loading ? 'Wait...' : 'View Report'}
             </button>
          </div>

          {(activeTab === 'daily' || activeTab === 'patient') && !loading && (
             <div className="summary-cards">
                <div className="s-card" style={{background: 'linear-gradient(135deg, #14b8a6, #047857)'}}>
                   <div className="s-label">OPD Sales</div>
                   <div className="s-value">₹ {summary.opd.gross.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-details">
                      <span className="text-white-50">Recv: ₹{summary.opd.received.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      <span className="text-warning fw-bold">Due: ₹{summary.opd.due.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                   </div>
                </div>

                <div className="s-card" style={{background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)'}}>
                   <div className="s-label">IPD Sales</div>
                   <div className="s-value">₹ {summary.ipd.gross.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-details">
                      <span className="text-white-50">Recv: ₹{summary.ipd.received.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      <span className="text-warning fw-bold">Due: ₹{summary.ipd.due.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                   </div>
                </div>

                <div className="s-card" style={{background: 'linear-gradient(135deg, #f59e0b, #b45309)'}}>
                   <div className="s-label">Pharmacy Sales</div>
                   <div className="s-value">₹ {summary.pharmacy.gross.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-details">
                      <span className="text-white-50">Recv: ₹{summary.pharmacy.received.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      <span className="text-warning fw-bold">Due: ₹{summary.pharmacy.due.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                   </div>
                </div>

                <div className="s-card" style={{background: 'linear-gradient(135deg, #a855f7, #7c3aed)'}}>
                   <div className="s-label">💰 Due Collections</div>
                   <div className="s-value">₹ {summary.dueCollected.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-details">
                      <span className="text-white-50">Cash: ₹{summary.dueCollectionCash.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      <span className="text-white fw-bold">UPI: ₹{summary.dueCollectionUpi.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                   </div>
                </div>

                <div className="s-card total">
                   <div className="s-label">Total Sales (Gross)</div>
                   <div className="s-value">₹ {summary.grossSales.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-sub">{summary.bills} Transactions</div>
                </div>
                
                <div className="s-card returns" style={{background: 'linear-gradient(135deg, #ef4444, #b91c1c)'}}>
                   <div className="s-label">Total Returns</div>
                   <div className="s-value">₹ {summary.returns.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-sub" style={{fontSize:'9px', fontWeight:'normal'}}>
                       {formatDate(filters.fromDate)} to {formatDate(filters.toDate)}
                   </div>
                </div>

                <div className="s-card net" style={{background: 'linear-gradient(135deg, #4f46e5, #1d4ed8)'}}>
                   <div className="s-label">Net Sales</div>
                   <div className="s-value">₹ {summary.netSales.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-sub">Gross - Returns</div>
                </div>

                <div className="s-card cash">
                   <div className="s-label">Net Cash (All)</div>
                   <div className="s-value">₹ {summary.cash.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-sub" style={{fontSize:'9px'}}>Sale + Due Collection</div>
                </div>

                <div className="s-card upi" style={{background: 'linear-gradient(135deg, #0ea5e9, #0284c7)'}}>
                   <div className="s-label">Net UPI (All)</div>
                   <div className="s-value">₹ {summary.upi.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   <div className="s-sub" style={{fontSize:'9px'}}>Sale + Due Collection</div>
                </div>
             </div>
          )}

          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  {activeTab === 'daily' && (
                     <tr><th className="text-start">Date</th><th className="text-start">Bill/Receipt No</th><th className="text-center">Txn Type</th><th className="text-center">Pat. Type</th><th className="text-start">Patient Name</th><th className="text-center">Mode</th><th className="text-end">Cash</th><th className="text-end">UPI</th><th className="text-end">Total</th><th className="text-start">User</th></tr>
                  )}
                  {activeTab === 'patient' && (
                     <tr><th className="text-start">Date</th><th className="text-start">Bill/Receipt No</th><th className="text-center">Txn Type</th><th className="text-center">Pat. Type</th><th className="text-start">Patient Name</th><th className="text-start">Mobile No</th><th className="text-end">Net Amount</th><th className="text-end text-danger">Due</th></tr>
                  )}
                  {activeTab === 'item-wise' && (
                     <tr><th className="text-start">Rank</th><th className="text-start">Medicine Name</th><th className="text-start">Unit</th><th className="text-center">Times Sold</th><th className="text-center">Qty Sold</th><th className="text-end">Revenue</th></tr>
                  )}
                  {activeTab === 'user-wise' && (
                     <tr><th className="text-start">Staff Name</th><th className="text-center">Total</th><th className="text-end">Cash Collected</th><th className="text-end">UPI Collected</th><th className="text-end">Total Activity</th></tr>
                  )}
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="empty-state">Generating Report...</td></tr>
                  ) : displayedData.length === 0 ? (
                    <tr><td colSpan="10" className="empty-state">No records found.</td></tr>
                  ) : (
                    displayedData.map((item, index) => {
                      const txnStyle = getTxnTypeBadgeStyle(item.transactionType);
                      return (
                      <tr key={index}>
                        {activeTab === 'daily' && <>
                           <td className="text-start text-muted">{formatDate(item.billDate)}</td>
                           <td className="text-start fw-bold text-primary">{item.billNo}</td>
                           <td className="text-center">
                              <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: txnStyle.bg, color: txnStyle.color, border: `1px solid ${txnStyle.border}` }}>
                                {getTxnTypeLabel(item.transactionType)}
                              </span>
                           </td>
                           <td className="text-center"><span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}>{getPatType(item.customerType)}</span></td>
                           <td className="text-start fw-medium">{item.patientName}</td>
                           <td className="text-center"><span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>{item.payMode || '-'}</span></td>
                           <td className={`text-end fw-medium ${item.cashAmount < 0 ? 'text-danger' : 'text-success'}`}>₹{item.cashAmount?.toFixed(2)}</td>
                           <td className={`text-end fw-medium ${item.upiAmount < 0 ? 'text-danger' : 'text-primary'}`}>₹{item.upiAmount?.toFixed(2)}</td>
                           <td className={`text-end fw-bold ${item.totalBillAmount < 0 ? 'text-danger' : ''}`}>₹{item.totalBillAmount?.toFixed(2)}</td>
                           <td className="text-start text-muted">{item.userName}</td>
                        </>}

                        {activeTab === 'patient' && <>
                           <td className="text-start text-muted">{formatDate(item.billDate)}</td>
                           <td className="text-start fw-bold text-primary">{item.billNo}</td>
                           <td className="text-center">
                              <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: txnStyle.bg, color: txnStyle.color, border: `1px solid ${txnStyle.border}` }}>
                                {getTxnTypeLabel(item.transactionType)}
                              </span>
                           </td>
                           <td className="text-center"><span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}>{getPatType(item.customerType)}</span></td>
                           <td className="text-start fw-medium">{item.patientName}</td>
                           <td className="text-start text-muted">{item.mobileNo || '-'}</td>
                           <td className={`text-end fw-bold ${item.totalBillAmount < 0 ? 'text-danger' : ''}`}>₹{item.totalBillAmount?.toFixed(2)}</td>
                           <td className="text-end text-danger fw-bold">{item.dueAmount > 0 ? `₹${item.dueAmount?.toFixed(2)}` : '-'}</td>
                        </>}

                        {activeTab === 'item-wise' && <>
                           <td className="text-start text-muted">#{(currentPage - 1) * rowsPerPage + index + 1}</td>
                           <td className="text-start fw-medium">{item.medicine}</td>
                           <td className="text-start text-muted">{item.unit}</td>
                           <td className="text-center">{item.timesSold}</td>
                           <td className="text-center fw-bold">{item.totalQtySold}</td>
                           <td className="text-end text-success fw-bold">₹{item.totalRevenue?.toFixed(2)}</td>
                        </>}

                        {activeTab === 'user-wise' && <>
                           <td className="text-start fw-bold">{item.userName}</td>
                           <td className="text-center">{item.totalBills}</td>
                           <td className="text-end text-success fw-medium">₹{item.totalCashCollection?.toFixed(2)}</td>
                           <td className="text-end text-primary fw-medium">₹{item.totalUPICollection?.toFixed(2)}</td>
                           <td className="text-end fw-bold">₹{item.totalSaleValue?.toFixed(2)}</td>
                        </>}
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>
            
            {!loading && reportData.length > 0 && (
              <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'12px'}}>Rows per page:</span>
                  <select 
                    className="form-select form-select-sm" 
                    style={{width: '70px', fontSize: '12px', padding: '2px 10px'}}
                    value={rowsPerPage} 
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'12px', marginRight:'10px'}}>
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, reportData.length)} of {reportData.length} entries
                  </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  ><i className="bi bi-chevron-left"></i> Prev</button>
                  <span className="fw-bold" style={{fontSize:'12px'}}> Page {currentPage} of {totalPages} </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === totalPages} 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >Next <i className="bi bi-chevron-right"></i></button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <style jsx>{`
        .report-container { padding: 0; font-size: 12px; }
        .report-card { background: white; border-radius: 8px; box-shadow: 0 1px 10px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; overflow: hidden; }
        
        .card-header { background: var(--theme-color, #0088cc); padding: 8px 15px; }
        .header-left { display: flex; align-items: center; gap: 10px; color: white; }
        .header-icon { width: 32px; height: 32px; background: rgba(255, 255, 255, 0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .header-text h5 { margin: 0; font-size: 15px; font-weight: 700; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 11px; }

        .tabs-header { display: flex; background: #f8fafc; border-bottom: 1px solid #e0e6ed; padding: 0 15px; gap: 5px; overflow-x: auto; }
        .tab-btn { padding: 10px 14px; border: none; background: transparent; color: #64748b; font-weight: 600; font-size: 12px; cursor: pointer; border-bottom: 2px solid transparent; display: flex; gap: 6px; align-items: center; white-space: nowrap; }
        .tab-btn:hover { color: var(--theme-color, #0088cc); }
        .tab-btn.active { color: var(--theme-color, #0088cc); border-bottom-color: var(--theme-color, #0088cc); background: white; border-radius: 6px 6px 0 0; border: 1px solid #e0e6ed; border-bottom: none; position: relative; top: 1px; }

        .card-content { padding: 15px; }
        .filter-bar { display: flex; gap: 8px; background: #f8fafc; padding: 10px; border-radius: 6px; border: 1px solid #e0e6ed; margin-bottom: 12px; align-items: flex-end; flex-wrap: wrap; }
        .form-group { display: flex; flex-direction: column; min-width: 120px; }
        .form-group label { font-size: 11px; font-weight: 600; margin-bottom: 3px; color: #475569; }
        .form-input { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 12px; height: 30px; outline: none; }
        .form-input:focus { border-color: var(--theme-color, #0088cc); }
        
        .btn-search { padding: 0 15px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-weight: 600; cursor: pointer; height: 30px; font-size: 12px; transition: 0.2s; }
        .btn-search:hover { background: #0077b3; }

        .summary-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 15px; }
        .s-card { padding: 12px; border-radius: 8px; color: white; position: relative; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .s-card.total { background: linear-gradient(135deg, #3b82f6, #2563eb); }
        .s-card.cash { background: linear-gradient(135deg, #10b981, #059669); }
        .s-card.upi { background: linear-gradient(135deg, #0ea5e9, #0284c7); }
        
        .s-label { font-size: 11px; opacity: 0.9; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px; }
        .s-value { font-size: 18px; font-weight: 700; }
        .s-sub { font-size: 10px; opacity: 0.8; margin-top: 2px; }
        
        .s-details { display: flex; justify-content: space-between; font-size: 10px; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.2); }
        .text-white-50 { color: rgba(255, 255, 255, 0.7); }
        .text-warning { color: #fde047 !important; }

        .table-section { background: white; border-radius: 6px; border: 1px solid #e0e6ed; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .custom-table th { background: #f8fafc; color: #334155; padding: 8px 10px; font-weight: 600; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #1e293b; }
        .custom-table tr:hover { background-color: #f8fafc; }

        .text-start { text-align: left !important; }
        .text-center { text-align: center !important; }
        .text-end { text-align: right !important; }
        
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 500; }
        .text-success { color: #16a34a !important; }
        .text-danger { color: #dc2626 !important; }
        .text-primary { color: var(--theme-color, #0088cc) !important; }
        .text-muted { color: #64748b !important; }

        .empty-state { text-align: center; padding: 30px; color: #94a3b8; font-style: italic; }
      `}</style>
    </div>
  );
};

export default SalesReports;